import React, { useState, useEffect, useMemo, memo } from 'react'
import Modal from '@cloudscape-design/components/modal'
import Box from '@cloudscape-design/components/box'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Button from '@cloudscape-design/components/button'
import FormField from '@cloudscape-design/components/form-field'
import Input from '@cloudscape-design/components/input'
import Alert from '@cloudscape-design/components/alert'
import { sanitizeText, isValidAWSUserid, isValidApprovalId } from '../../utils/validation'

/**
 * AddDocumentDialog component for adding a single document
 * Shows real-time DID matching to help ensure uniqueness
 */
const AddDocumentDialog = memo(({ 
  isOpen, 
  onClose, 
  onSubmit, 
  existingDIDs,
  isProcessing 
}) => {
  const [formData, setFormData] = useState({
    did: '',
    title: '',
    location: '',
    approvalId: '',
    owner: '',
    status: ''
  })
  
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})

  // Find matching DIDs in real-time
  const matchingDIDs = useMemo(() => {
    if (!formData.did || formData.did.length < 2) {
      return []
    }
    
    const searchTerm = formData.did.toLowerCase()
    return existingDIDs
      .filter(did => did.toLowerCase().includes(searchTerm))
      .slice(0, 10)
  }, [formData.did, existingDIDs])

  // Check if DID already exists
  const didExists = useMemo(() => {
    return formData.did && existingDIDs.includes(formData.did)
  }, [formData.did, existingDIDs])

  // Validate form
  useEffect(() => {
    const newErrors = {}
    
    if (touched.did) {
      if (!formData.did || formData.did.trim() === '') {
        newErrors.did = 'DID is required'
      } else if (didExists) {
        newErrors.did = 'DID already exists in database'
      }
    }
    
    if (touched.location && formData.location && formData.location.trim() !== '') {
      try {
        const url = new URL(formData.location)
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
          newErrors.location = 'URL must use http or https protocol'
        }
      } catch {
        newErrors.location = 'Invalid URL format'
      }
    }
    
    if (touched.owner) {
      if (!formData.owner || formData.owner.trim() === '') {
        newErrors.owner = 'Owner is required'
      } else if (!isValidAWSUserid(formData.owner)) {
        newErrors.owner = 'Must be a valid AWS userid (lowercase letters, numbers, .-_ only)'
      }
    }

    if (touched.approvalId && formData.approvalId && formData.approvalId.trim() !== '') {
      if (!isValidApprovalId(formData.approvalId)) {
        newErrors.approvalId = 'Must be numeric only, between 6 and 10 digits'
      }
    }
    
    setErrors(newErrors)
  }, [formData, touched, didExists])

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setTouched(prev => ({ ...prev, [field]: true }))
  }

  const handleSubmit = () => {
    setTouched({
      did: true,
      title: true,
      location: true,
      approvalId: true,
      owner: true,
      status: true
    })

    if (!formData.did || !formData.owner) {
      return
    }

    if (Object.keys(errors).length > 0 || didExists) {
      return
    }

    const sanitizedData = {
      did: sanitizeText(formData.did),
      title: sanitizeText(formData.title) || '',
      location: sanitizeText(formData.location) || '',
      approvalId: sanitizeText(formData.approvalId) || '',
      owner: sanitizeText(formData.owner),
      status: sanitizeText(formData.status) || 'Draft',
      filename: ''
    }

    if (formData.location && formData.location.trim()) {
      try {
        const url = new URL(formData.location)
        const pathSegments = url.pathname.split('/').filter(s => s)
        sanitizedData.filename = pathSegments.length > 0 
          ? pathSegments[pathSegments.length - 1] 
          : formData.location
      } catch {
        sanitizedData.filename = formData.location
      }
    }

    onSubmit(sanitizedData)
  }

  const handleClose = () => {
    if (!isProcessing) {
      setFormData({
        did: '',
        title: '',
        location: '',
        approvalId: '',
        owner: '',
        status: ''
      })
      setErrors({})
      setTouched({})
      onClose()
    }
  }

  const isFormValid = Object.keys(errors).length === 0 && 
                      formData.did && formData.owner && !didExists

  return (
    <Modal
      visible={isOpen}
      onDismiss={handleClose}
      header="Add New Document ID"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleClose} disabled={isProcessing}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={!isFormValid || isProcessing}>
              {isProcessing ? 'Adding...' : 'Add Document'}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <FormField
          label="DID"
          errorText={errors.did}
          constraintText="Required"
        >
          <Input
            value={formData.did}
            onChange={({ detail }) => handleChange('did', detail.value)}
            placeholder="e.g., AW1AA0001"
            disabled={isProcessing}
            invalid={!!errors.did}
          />
        </FormField>

        {matchingDIDs.length > 0 && !didExists && (
          <Alert type="warning" header={`Similar DIDs found (${matchingDIDs.length})`}>
            <Box variant="p">Make sure your DID is unique:</Box>
            <ul>
              {matchingDIDs.map((did, idx) => (
                <li key={idx}>{did}</li>
              ))}
            </ul>
          </Alert>
        )}

        <FormField label="Title">
          <Input
            value={formData.title}
            onChange={({ detail }) => handleChange('title', detail.value)}
            placeholder="Document title"
            disabled={isProcessing}
          />
        </FormField>

        <FormField
          label="Authoritative URL"
          errorText={errors.location}
        >
          <Input
            value={formData.location}
            onChange={({ detail }) => handleChange('location', detail.value)}
            placeholder="https://example.com/document.pdf"
            type="url"
            disabled={isProcessing}
            invalid={!!errors.location}
          />
        </FormField>

        <FormField
          label="Approval ID"
          errorText={errors.approvalId}
          constraintText="Optional. Numeric only, 6-10 digits."
        >
          <Input
            value={formData.approvalId}
            onChange={({ detail }) => handleChange('approvalId', detail.value.replace(/[^0-9]/g, ''))}
            placeholder="e.g., 12345678"
            disabled={isProcessing}
            invalid={!!errors.approvalId}
            inputMode="numeric"
          />
        </FormField>

        <FormField
          label="Owner"
          errorText={errors.owner}
          constraintText="Required. Enter a valid AWS userid."
        >
          <Input
            value={formData.owner}
            onChange={({ detail }) => handleChange('owner', detail.value)}
            placeholder="e.g., jdoe or john-doe"
            disabled={isProcessing}
            invalid={!!errors.owner}
          />
        </FormField>

        <FormField label="Status">
          <Input
            value={formData.status}
            onChange={({ detail }) => handleChange('status', detail.value)}
            placeholder="e.g., Draft, Current, Archived"
            disabled={isProcessing}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  )
})

AddDocumentDialog.displayName = 'AddDocumentDialog'

export default AddDocumentDialog
